import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { csvCell } from '../../lib/csvCell';
import Modal from '../shared/Modal';
import { auth } from '../../lib/firebase';
import { notifyToast } from '../../lib/toastStore';
import { workspaceHeader } from '../../lib/workspaces';
import type { Application } from '../../types';
import {
  BadgeDollarSign, Search, Filter, Eye, CheckCircle2, XCircle, Wallet, Upload, Paperclip,
  Loader, Users, Clock, TrendingUp, AlertTriangle, User as UserIcon, Building2,
  ChevronLeft, ChevronRight, Download, Info, ExternalLink, Ban, Flag, Lock, RefreshCw
} from 'lucide-react';

const wsFetch = (url: string, options?: RequestInit): Promise<Response> => {
  return fetch(url, {
    ...options,
    headers: { ...workspaceHeader(), ...(options?.headers as Record<string, string>) },
  });
};

interface CommissionRecord {
  id: string;
  commissionId?: string;
  referrerId?: string;
  referrerName?: string;
  referrerType?: string;
  referrerEmail?: string;
  referrerCompany?: string;
  applicationId?: string;
  applicantId?: string;
  applicantName?: string;
  applicantEmail?: string;
  applicantPhotoUrl?: string;
  program?: string;
  programId?: string;
  amount?: number;
  rate?: number;
  currency?: string;
  commissionStatus?: string;
  payoutStatus?: string;
  payoutProcessedAt?: string | null;
  paidAt?: string | null;
  payment?: any;
  invoiceExists?: boolean;
  balanceAmount?: number;
  balanceCheck?: string;
  readyToQueue?: boolean;
  candidateStatus?: string;
  stage?: string;
  cancelReason?: string;
  notes?: string;
  history?: any[];
  // Void marker (Finance tab "Remove Commission"): the record stays for
  // history but is excluded from every payout total.
  removedAt?: string | null;
  removedBy?: any;
  createdAt?: string;
  updatedAt?: string;
}

interface CommissionsManagerProps {
  applications: Application[];
  currency?: string;
  onViewApplicant?: (app: Application) => void;
}

const COMMISSION_STATUSES = ['not_committed', 'committed'];
const PAYOUT_STATUSES = ['not_queued', 'queued', 'cancelled'];
const PAYOUT_METHODS = [
  'Bank Transfer', 'Wire Transfer', 'PayPal', 'Western Union', 'Check', 'Cash',
  'Crypto', 'Mobile Money', 'Other',
];

const fmtNum = (val: any): number => {
  const n = Number(val);
  return isNaN(n) ? 0 : n;
};

const commLabel = (s?: string) => (s === 'committed' ? 'Committed' : 'Not Committed');
const payLabel = (s?: string) => (s === 'cancelled' ? 'Cancelled' : s === 'queued' ? 'Queued' : 'Not Queued');

export default function CommissionsManager({ applications, currency = 'USD', onViewApplicant }: CommissionsManagerProps) {
  const [commissions, setCommissions] = useState<CommissionRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState('');
  const [commFilter, setCommFilter] = useState('All');
  const [payFilter, setPayFilter] = useState('All');
  const [balanceFilter, setBalanceFilter] = useState('All');
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;

  const [selected, setSelected] = useState<CommissionRecord | null>(null);
  const [cancelFor, setCancelFor] = useState<CommissionRecord | null>(null);
  const [removeFor, setRemoveFor] = useState<CommissionRecord | null>(null);
  const [amountDraft, setAmountDraft] = useState('');
  const [editAmountOpen, setEditAmountOpen] = useState(false);
  const [processFor, setProcessFor] = useState<CommissionRecord | null>(null);

  // Process-payout form state
  const [procTransactionId, setProcTransactionId] = useState('');
  const [procMethod, setProcMethod] = useState('');
  const [procAmount, setProcAmount] = useState('');
  const [procDate, setProcDate] = useState('');
  const [procNote, setProcNote] = useState('');
  const [procFile, setProcFile] = useState<{ name: string; dataUrl: string; size: number } | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [cancelNote, setCancelNote] = useState('');

  const symbolMap: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', CAD: '$', AUD: '$', ZAR: 'R', NGN: '₦', KES: 'KSh', GHS: '₵', INR: '₹' };
  const symbol = symbolMap[currency] || '$';
  const fmtMoney = (val: any): string => `${symbol}${fmtNum(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const isProcessed = (c: CommissionRecord): boolean => !!(c.payoutProcessedAt || c.payment?.transactionId || c.paidAt);
  const isCancellable = (c: CommissionRecord): boolean => c.payoutStatus !== 'cancelled' && !isProcessed(c);
  const isQueueableReady = (c: CommissionRecord): boolean =>
    c.commissionStatus === 'committed' && c.invoiceExists === true && fmtNum(c.balanceAmount) === 0 &&
    c.payoutStatus !== 'queued' && c.payoutStatus !== 'cancelled' && !isProcessed(c);

  /** All applicable reasons that block Queue — exact spec wording. */
  const queueBlockReasons = (c: CommissionRecord): string[] => {
    const reasons: string[] = [];
    if (c.payoutStatus === 'cancelled') { reasons.push('Cannot queue — commission has been cancelled.'); return reasons; }
    if (c.payoutStatus === 'queued') { reasons.push('Cannot queue — commission is already queued.'); return reasons; }
    if (isProcessed(c)) { reasons.push('Cannot queue — payout has already been processed.'); return reasons; }
    if (c.commissionStatus !== 'committed') reasons.push('Cannot queue — commission has not been committed yet.');
    if (!c.invoiceExists) reasons.push('Cannot queue — no invoice exists for this applicant.');
    else if (fmtNum(c.balanceAmount) !== 0) reasons.push(`Cannot queue — applicant has an outstanding balance of ${fmtMoney(c.balanceAmount)}.`);
    return reasons;
  };

  const loadCommissions = useCallback(async () => {
    setLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch('/api/admin/finance/commissions', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Request failed (${res.status})`);
      }
      const data = await res.json();
      setCommissions(data.commissions || []);
    } catch (e: any) {
      console.warn('Failed to fetch commissions:', e.message);
      notifyToast('error', 'Failed to load commissions: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCommissions(); }, [loadCommissions]);

  // Reset the inline amount editor whenever a different record is opened.
  useEffect(() => { setEditAmountOpen(false); setAmountDraft(''); }, [selected?.id]);

  const stats = useMemo(() => {
    // Removed (voided) commissions stay listed for history but never
    // count toward payout totals or actionable counts.
    const live = commissions.filter(c => !c.removedAt);
    const total = commissions.length;
    const committed = live.filter(c => c.commissionStatus === 'committed' && c.payoutStatus !== 'cancelled' && !isProcessed(c)).length;
    const readyCount = live.filter(c => isQueueableReady(c)).length;
    const queued = live.filter(c => c.payoutStatus === 'queued' && !isProcessed(c));
    const queuedCount = queued.length;
    const queuedAmt = queued.reduce((s, c) => s + fmtNum(c.amount), 0);
    const processed = live.filter(c => isProcessed(c));
    const processedAmt = processed.reduce((s, c) => s + fmtNum(c.amount), 0);
    const cancelled = live.filter(c => c.payoutStatus === 'cancelled').length;
    const committedCount = live.filter(c => c.commissionStatus === 'committed').length;
    return { total, committed, readyCount, queuedCount, queuedAmt, processedAmt, cancelled, committedCount };
  }, [commissions]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return commissions.filter(c => {
      if (commFilter !== 'All' && (c.commissionStatus || 'not_committed') !== commFilter) return false;
      if (payFilter !== 'All') {
        if (payFilter === 'processed') { if (!isProcessed(c)) return false; }
        else if ((c.payoutStatus || 'not_queued') !== payFilter) return false;
      }
      if (balanceFilter === 'Pass' && c.balanceCheck !== 'pass') return false;
      if (balanceFilter === 'Pending' && c.balanceCheck !== 'pending') return false;
      if (q) {
        const hay = [
          c.applicantName, c.applicantEmail, c.program, c.referrerName, c.referrerCompany,
          c.referrerEmail, c.id, c.applicationId,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [commissions, search, commFilter, payFilter, balanceFilter]);

  useEffect(() => { setPage(1); }, [search, commFilter, payFilter, balanceFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageRows = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const commBadge = (c: CommissionRecord) => (
    c.removedAt ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-extrabold border bg-red-50 text-red-800 border-red-200">
        Removed
      </span>
    ) : (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-extrabold border ${
      c.commissionStatus === 'committed' ? 'bg-blue-50 text-blue-800 border-blue-200' : 'bg-slate-100 text-slate-600 border-slate-200'
    }`}>
      {commLabel(c.commissionStatus)}
    </span>
    )
  );

  const payBadge = (c: CommissionRecord) => {
    if (c.payoutStatus === 'cancelled') return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-extrabold border bg-rose-50 text-rose-800 border-rose-200">Cancelled</span>;
    if (c.payoutStatus === 'queued') return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-extrabold border bg-purple-50 text-purple-800 border-purple-200">Queued</span>;
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-extrabold border bg-slate-100 text-slate-600 border-slate-200">Not Queued</span>;
  };

  const processedTag = (c: CommissionRecord) => (
    c.payoutStatus !== 'cancelled' && isProcessed(c) ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-extrabold border bg-emerald-50 text-emerald-800 border-emerald-200 gap-1">
        <CheckCircle2 className="h-3 w-3" /> Payout Processed
      </span>
    ) : null
  );

  const readinessCell = (c: CommissionRecord) => {
    if (c.payoutStatus === 'cancelled') {
      return <span className="text-[10px] text-rose-600 font-semibold italic">Commission cancelled</span>;
    }
    if (isProcessed(c)) {
      return <span className="text-[10px] text-emerald-700 font-semibold">Payout processed {c.payoutProcessedAt ? `on ${new Date(c.payoutProcessedAt).toLocaleDateString()}` : ''}</span>;
    }
    if (c.commissionStatus === 'committed' && c.payoutStatus !== 'queued') {
      const reasons = queueBlockReasons(c);
      if (reasons.length === 0) {
        return (
          <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
            <CheckCircle2 className="h-3 w-3" /> Ready to Queue
          </span>
        );
      }
      return (
        <div className="space-y-0.5 max-w-[220px]">
          {reasons.map((r, i) => (
            <p key={i} className="text-[11px] leading-tight text-amber-700 flex items-start gap-1">
              <AlertTriangle className="h-2.5 w-2.5 mt-0.5 shrink-0" /> {r}
            </p>
          ))}
        </div>
      );
    }
    if (c.payoutStatus === 'queued') {
      return <span className="text-[10px] text-purple-700 font-semibold">Queued — awaiting payout</span>;
    }
    return <span className="text-[11px] text-slate-500">Awaiting admin review</span>;
  };

  const patchCommission = (updated: CommissionRecord) => {
    setCommissions(prev => prev.map(c => (c.id === updated.id ? { ...c, ...updated } : c)));
  };

  const syncSelected = (updated: CommissionRecord) => {
    if (selected && selected.id === updated.id) setSelected({ ...selected, ...updated });
  };

  const runCommit = async (c: CommissionRecord) => {
    setSaving(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/commissions/${c.id}/commit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      patchCommission(data.commission);
      syncSelected(data.commission);
      notifyToast('success', 'Commission committed');
    } catch (e: any) {
      console.warn('Commit error:', e.message);
      notifyToast('error', e.message || 'Failed to commit commission');
    } finally {
      setSaving(false);
    }
  };

  const runQueue = async (c: CommissionRecord) => {
    setSaving(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/commissions/${c.id}/queue`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) {
        const reasons: string[] = data.reasons || [];
        const msg = reasons.length > 0 ? reasons.join(' ') : (data.error || `Request failed (${res.status})`);
        throw new Error(msg);
      }
      patchCommission(data.commission);
      syncSelected(data.commission);
      notifyToast('success', 'Commission queued for payout');
    } catch (e: any) {
      console.warn('Queue error:', e.message);
      notifyToast('error', e.message || 'Failed to queue commission');
    } finally {
      setSaving(false);
    }
  };

  const runCancel = async (c: CommissionRecord) => {
    setSaving(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/commissions/${c.id}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: cancelNote.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      patchCommission(data.commission);
      syncSelected(data.commission);
      notifyToast('success', 'Commission cancelled');
      setCancelFor(null);
      setCancelNote('');
    } catch (e: any) {
      console.warn('Cancel error:', e.message);
      notifyToast('error', e.message || 'Failed to cancel commission');
    } finally {
      setSaving(false);
    }
  };

  // Manual override (the ONLY exception to "stamped amounts never
  // change"): an admin edits this applicant's stamped commission amount —
  // including down to $0. The server records old → new with attribution
  // in the record history and re-stamps the application.
  const runAmountOverride = async (c: CommissionRecord) => {
    const n = Number(amountDraft);
    if (amountDraft.trim() === '' || !isFinite(n) || n < 0) {
      notifyToast('error', 'Enter a valid amount (0 is allowed).');
      return;
    }
    setSaving(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/commissions/${c.id}/amount`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: n }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      patchCommission(data.commission);
      syncSelected(data.commission);
      setEditAmountOpen(false);
      notifyToast('success', 'Commission amount updated');
    } catch (e: any) {
      console.warn('Amount override error:', e.message);
      notifyToast('error', e.message || 'Failed to update commission amount');
    } finally {
      setSaving(false);
    }
  };

  // Remove the commission from the applicant entirely (void, not a hard
  // delete): the applicant stays linked to the referrer, who earns
  // nothing for them; the record remains visible in history.
  const runRemove = async (c: CommissionRecord) => {
    setSaving(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/commissions/${c.id}/remove`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      patchCommission(data.commission);
      syncSelected(data.commission);
      notifyToast('success', 'Commission removed from applicant');
      setRemoveFor(null);
    } catch (e: any) {
      console.warn('Remove commission error:', e.message);
      notifyToast('error', e.message || 'Failed to remove commission');
    } finally {
      setSaving(false);
    }
  };

  const openProcessModal = (c: CommissionRecord) => {
    setProcessFor(c);
    setProcTransactionId('');
    setProcMethod('');
    setProcAmount(String(fmtNum(c.amount) || ''));
    setProcDate(new Date().toISOString().slice(0, 10));
    setProcNote('');
    setProcFile(null);
  };

  const uploadProcFile = async (): Promise<any | null> => {
    if (!procFile) return null;
    setUploadingFile(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch('/api/admin/finance/upload-attachment', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: procFile.name, fileData: procFile.dataUrl, mimeType: 'application/octet-stream' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to upload file');
      return data.attachment;
    } finally {
      setUploadingFile(false);
    }
  };

  const submitProcessed = async () => {
    if (!processFor) return;
    if (!procTransactionId.trim()) { notifyToast('error', 'Transaction ID is required'); return; }
    if (!procMethod) { notifyToast('error', 'Payout method is required'); return; }
    if (!procAmount || fmtNum(procAmount) <= 0) { notifyToast('error', 'A valid payout amount is required'); return; }
    if (!procDate) { notifyToast('error', 'Payout date is required'); return; }
    setSaving(true);
    try {
      const attachment = procFile ? await uploadProcFile() : null;
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/commissions/${processFor.id}/processed`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transactionId: procTransactionId.trim(),
          payoutMethod: procMethod,
          amount: fmtNum(procAmount),
          payoutDate: procDate,
          note: procNote.trim(),
          attachment,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      patchCommission(data.commission);
      syncSelected(data.commission);
      notifyToast('success', 'Payout recorded as processed');
      setProcessFor(null);
      setProcFile(null);
    } catch (e: any) {
      console.warn('Process payout error:', e.message);
      notifyToast('error', e.message || 'Failed to record processed payout');
    } finally {
      setSaving(false);
    }
  };

  const pickProcFile = (file: File) => {
    if (file.size > 12 * 1024 * 1024) { notifyToast('error', 'File must be 12MB or smaller'); return; }
    const reader = new FileReader();
    reader.onload = () => setProcFile({ name: file.name, dataUrl: String(reader.result), size: file.size });
    reader.onerror = () => notifyToast('error', 'Could not read file');
    reader.readAsDataURL(file);
  };

  const exportCsv = () => {
    const headers = ['Applicant', 'Program', 'Referrer', 'Referrer Type', 'Commission Amount', 'Commission Status', 'Payout Status', 'Payout Processed', 'Balance Check', 'Balance Amount', 'Invoice Exists', 'Updated'];
    const rows = filtered.map(c => [
      c.applicantName || '', c.program || '', c.referrerName || '', c.referrerType || 'Regular Referrer',
      fmtNum(c.amount).toFixed(2), commLabel(c.commissionStatus), payLabel(c.payoutStatus),
      isProcessed(c) ? 'Yes' : 'No', c.balanceCheck === 'pass' ? 'Pass' : 'Pending', fmtNum(c.balanceAmount).toFixed(2),
      c.invoiceExists ? 'Yes' : 'No', c.updatedAt || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => csvCell(v)).join(',')).join('\n');
    const link = document.createElement('a');
    link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    link.download = `Commissions_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const applicantApplication = (c: CommissionRecord): Application | undefined => {
    if (!c.applicantId && !c.applicationId) return undefined;
    return (applications || []).find(a => a.applicationId === c.applicationId || (!!c.applicantId && a.userId === c.applicantId));
  };

  const ActionButtons = ({ c, compact }: { c: CommissionRecord; compact?: boolean }) => {
    const reasons = queueBlockReasons(c);
    const showQueue = c.payoutStatus !== 'queued' && !isProcessed(c) && c.payoutStatus !== 'cancelled';
    const showCancel = isCancellable(c);
    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        <button type="button" onClick={() => setSelected(c)} className={`px-2 py-1 text-[10px] font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition cursor-pointer flex items-center gap-1`}>
          <Eye className="h-3 w-3" /> View
        </button>
        {c.commissionStatus !== 'committed' && showCancel && (
          <button type="button" onClick={() => runCommit(c)} disabled={saving} className="px-2 py-1 text-[10px] font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition cursor-pointer disabled:opacity-50 flex items-center gap-1">
            <Flag className="h-3 w-3" /> Commit
          </button>
        )}
        {showQueue && (
          <span className="inline-flex items-center gap-1">
            <button
              type="button"
              onClick={() => { if (reasons.length === 0) runQueue(c); }}
              disabled={reasons.length > 0 || saving}
              title={reasons.length > 0 ? reasons.join('\n') : 'Queue for payout'}
              className="px-2 py-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition cursor-pointer disabled:opacity-45 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <Wallet className="h-3 w-3" /> Queue
            </button>
            {reasons.length > 0 && !compact && (
              <span className="relative group">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500 cursor-help" />
                <span className="absolute z-20 hidden group-hover:block right-0 top-4 w-60 bg-white text-slate-600 border border-slate-200 text-[11px] leading-snug rounded-lg p-2 shadow-lg">
                  {reasons.map((r, i) => <span key={i} className="block">{r}</span>)}
                </span>
              </span>
            )}
          </span>
        )}
        {c.payoutStatus === 'queued' && !isProcessed(c) && (
          <button type="button" onClick={() => openProcessModal(c)} className="px-2 py-1 text-[10px] font-bold text-slate-800 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition cursor-pointer flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> Mark Processed
          </button>
        )}
        {showCancel && (
          <button type="button" onClick={() => { setCancelFor(c); setCancelNote(''); }} disabled={saving} className="px-2 py-1 text-[10px] font-bold text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-lg transition cursor-pointer disabled:opacity-50 flex items-center gap-1">
            <Ban className="h-3 w-3" /> Cancel
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-black text-slate-900 font-display flex items-center gap-2">
            <BadgeDollarSign className="h-5 w-5 text-emerald-600" />
            Commission &amp; Payout Ledger
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Commissions for applicants referred by active referrers. Queue requires a committed commission + a cleared applicant balance. Cancellation is always available pre-payout and is terminal.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition cursor-pointer">
            <Download className="h-4 w-4" /> Export CSV
          </button>
          <button onClick={loadCommissions} className="p-2 text-slate-500 hover:bg-slate-100 rounded-xl transition cursor-pointer border border-slate-200" title="Refresh">
            <RefreshIcon loading={loading} />
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-2xl font-black text-slate-900 font-mono">{stats.total}</span>
            <Users className="h-4 w-4 text-slate-400" />
          </div>
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 mt-1">Total Commissions</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-2xl font-black text-blue-600 font-mono">{stats.committed}</span>
            <Flag className="h-4 w-4 text-blue-500" />
          </div>
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 mt-1">Committed ({stats.committedCount} total)</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-2xl font-black text-emerald-600 font-mono">{stats.readyCount}</span>
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          </div>
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 mt-1">Ready to Queue</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-2xl font-black text-purple-600 font-mono">{stats.queuedCount}</span>
            <Clock className="h-4 w-4 text-purple-500" />
          </div>
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 mt-1">Queued ({fmtMoney(stats.queuedAmt)})</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-2xl font-black text-indigo-600 font-mono">{fmtMoney(stats.processedAmt)}</span>
            <TrendingUp className="h-4 w-4 text-indigo-500" />
          </div>
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 mt-1">Processed Payouts</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-2xl font-black text-rose-600 font-mono">{stats.cancelled}</span>
            <XCircle className="h-4 w-4 text-rose-500" />
          </div>
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 mt-1">Cancelled</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs p-4 space-y-3">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search applicant, referrer, program, commission ID..."
              className="w-full pl-10 pr-4 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 bg-white"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <div className="flex items-center gap-1.5 font-bold text-slate-500">
              <Filter className="h-3.5 w-3.5" /> Commission:
            </div>
            <select value={commFilter} onChange={e => setCommFilter(e.target.value)} className="px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white font-semibold focus:outline-none cursor-pointer focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary">
              <option value="All">All</option>
              <option value="not_committed">Not Committed</option>
              <option value="committed">Committed</option>
            </select>
            <div className="flex items-center gap-1.5 font-bold text-slate-500 ml-1">
              <Wallet className="h-3.5 w-3.5" /> Payout:
            </div>
            <select value={payFilter} onChange={e => setPayFilter(e.target.value)} className="px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white font-semibold focus:outline-none cursor-pointer focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary">
              <option value="All">All</option>
              <option value="not_queued">Not Queued</option>
              <option value="queued">Queued</option>
              <option value="processed">Processed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <div className="flex items-center gap-1.5 font-bold text-slate-500 ml-1">
              <Lock className="h-3 w-3" /> Balance:
            </div>
            <select value={balanceFilter} onChange={e => setBalanceFilter(e.target.value)} className="px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white font-semibold focus:outline-none cursor-pointer focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary">
              <option value="All">All</option>
              <option value="Pass">Pass</option>
              <option value="Pending">Pending</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider font-semibold border-b border-slate-200">
              <tr>
                <th className="px-4 py-3">Applicant / Program</th>
                <th className="px-4 py-3">Referrer</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Commission</th>
                <th className="px-4 py-3">Payout</th>
                <th className="px-4 py-3">Balance &amp; Readiness</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && commissions.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-14 text-center text-slate-400">
                  <Loader className="h-5 w-5 animate-spin inline-block mr-2" /> Loading commissions…
                </td></tr>
              ) : pageRows.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-14 text-center text-slate-400">
                  <div className="flex flex-col items-center gap-2">
                    <BadgeDollarSign className="h-8 w-8 text-slate-300" />
                    No commission records found for the current filters.
                  </div>
                </td></tr>
              ) : pageRows.map(c => (
                <tr key={c.id} onClick={() => setSelected(c)} className="hover:bg-slate-50/80 transition cursor-pointer align-top">
                  <td className="px-4 py-3">
                    <p className="font-bold text-slate-900">{c.applicantName || 'Applicant'}</p>
                    <p className="text-[11px] text-slate-500">{c.program || '—'}</p>
                    <p className="text-[11px] text-slate-500">{c.applicantEmail || ''}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-slate-800">{c.referrerName || 'Referrer'}</p>
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[11px] font-bold mt-0.5 ${(c.referrerType || '').toLowerCase().includes('enhanced') ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}>
                      {c.referrerType || 'Regular Referrer'}
                    </span>
                    {(c as any).referrerRemoved && (
                      <span className="inline-flex px-1.5 py-0.5 rounded text-[11px] font-bold mt-0.5 ml-1 bg-amber-100 text-amber-800 border border-amber-200">
                        Organisation removed
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-slate-900 whitespace-nowrap">{fmtMoney(c.amount)}</td>
                  <td className="px-4 py-3">{commBadge(c)}</td>
                  <td className="px-4 py-3 space-y-1">{payBadge(c)}{processedTag(c)}</td>
                  <td className="px-4 py-3">{readinessCell(c)}</td>
                  <td className="px-4 py-3">
                    <div onClick={e => e.stopPropagation()}><ActionButtons c={c} /></div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length > PER_PAGE && (
          <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
            <p className="text-[11px] text-slate-500 font-medium">Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length}</p>
            <div className="flex items-center gap-1">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-2.5 py-1 text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-lg disabled:opacity-40 cursor-pointer transition flex items-center gap-1">
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} onClick={() => setPage(p)} className={`px-3 py-1 text-xs font-bold rounded-lg cursor-pointer transition ${page === p ? 'bg-success text-white shadow-sm' : 'text-slate-600 hover:bg-slate-200'}`}>{p}</button>
              ))}
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-2.5 py-1 text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-lg disabled:opacity-40 cursor-pointer transition flex items-center gap-1">
                Next <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <Modal onClose={() => setSelected(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 space-y-5 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600"><BadgeDollarSign className="h-5 w-5" /></div>
                <div>
                  <h3 className="text-base font-black text-slate-900">Commission Record</h3>
                  <p className="text-[11px] text-slate-500 font-mono">ID: {selected.id}</p>
                </div>
              </div>
              <button aria-label="Close" title="Close" type="button" onClick={() => setSelected(null)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 cursor-pointer"><XCircle className="h-5 w-5" /></button>
            </div>

            <div className="space-y-4 text-xs">
              {/* Status banner */}
              <div className={`rounded-xl p-3.5 border space-y-2 ${selected.payoutStatus === 'cancelled' ? 'bg-rose-50 border-rose-200' : isProcessed(selected) ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Status Snapshot</p>
                  <div className="flex items-center gap-1.5 flex-wrap">{commBadge(selected)}{payBadge(selected)}{processedTag(selected)}</div>
                </div>
                {selected.payoutStatus === 'cancelled' ? (
                  <p className="text-rose-700 font-medium">{selected.cancelReason ? `Cancelled — ${selected.cancelReason}` : 'This commission has been cancelled. It is terminal and cannot be reopened.'}</p>
                ) : isProcessed(selected) ? (
                  <p className="text-emerald-800 font-medium">Payout processed {selected.payoutProcessedAt ? `on ${new Date(selected.payoutProcessedAt).toLocaleDateString()}` : ''} — no further action needed.</p>
                ) : selected.payoutStatus === 'queued' ? (
                  <p className="text-purple-700 font-medium">Queued — awaiting payout processing.</p>
                ) : selected.commissionStatus === 'committed' && isQueueableReady(selected) ? (
                  <p className="text-emerald-800 font-medium flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> Ready to Queue — committed and the applicant's balance is clear. Queue when you are ready to pay.</p>
                ) : selected.commissionStatus === 'committed' ? (
                  <div>
                    <p className="text-slate-700 font-semibold mb-1">Committed but not ready to queue:</p>
                    <ul className="space-y-1">
                      {queueBlockReasons(selected).map((r, i) => (
                        <li key={i} className="text-amber-800 flex items-start gap-1.5"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {r}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-slate-600">Not committed yet. Review the applicant and commit when ready.</p>
                )}
              </div>

              {/* Applicant card */}
              <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1"><UserIcon className="h-3 w-3" /> Applicant</p>
                </div>
                <div className="flex items-center gap-3">
                  {selected.applicantPhotoUrl ? (
                    <img src={selected.applicantPhotoUrl} alt="" className="h-10 w-10 rounded-full object-cover" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-black">{String(selected.applicantName || 'A').charAt(0)}</div>
                  )}
                  <div>
                    <p className="font-bold text-slate-900">{selected.applicantName || 'Applicant'}</p>
                    <p className="text-[11px] text-slate-500">{selected.applicantEmail || 'No email'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-200/60">
                  <span className="text-slate-600">Program: <strong>{selected.program || '—'}</strong></span>
                  <span className="text-slate-600">Application ID: <strong className="font-mono">{selected.applicationId || '—'}</strong></span>
                </div>
              </div>

              {/* Referrer card */}
              <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200 space-y-2">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1"><Building2 className="h-3 w-3" /> Referrer</p>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-bold text-slate-900">{selected.referrerName || 'Referrer'}</p>
                    {(selected as any).referrerRemoved && (
                      <span className="inline-flex px-1.5 py-0.5 rounded text-[11px] font-bold mt-0.5 bg-amber-100 text-amber-800 border border-amber-200">
                        Organisation removed — preserved history
                      </span>
                    )}
                    <p className="text-[11px] text-slate-500">{selected.referrerCompany || '—'}</p>
                    <p className="text-[11px] text-slate-500">{selected.referrerEmail || 'No email on file'}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-extrabold border ${(selected.referrerType || '').toLowerCase().includes('enhanced') ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                      {selected.referrerType || 'Regular Referrer'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Status / balance grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white rounded-xl p-3 border border-slate-200">
                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Commission Amount</p>
                  <p className="font-mono font-black text-emerald-700 text-sm">{fmtMoney(selected.amount)}</p>
                  {!selected.removedAt && !isProcessed(selected) && selected.payoutStatus !== 'cancelled' && (
                    editAmountOpen ? (
                      <div className="mt-2 flex items-center gap-1.5">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={amountDraft}
                          onChange={e => setAmountDraft(e.target.value)}
                          aria-label="New commission amount"
                          className="w-24 px-2 py-1 border border-slate-200 rounded-lg text-xs font-bold text-slate-900 focus:outline-none focus:ring-1 focus:ring-brand-primary"
                        />
                        <button onClick={() => runAmountOverride(selected)} disabled={saving} className="px-2 py-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-[11px] font-bold transition cursor-pointer">Save</button>
                        <button onClick={() => setEditAmountOpen(false)} className="px-2 py-1 text-slate-500 hover:bg-slate-100 rounded-lg text-[11px] font-bold transition cursor-pointer">Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => { setAmountDraft(String(fmtNum(selected.amount))); setEditAmountOpen(true); }} className="mt-1.5 text-[11px] font-bold text-blue-600 hover:text-blue-800 underline cursor-pointer">Edit amount</button>
                    )
                  )}
                </div>
                <div className="bg-white rounded-xl p-3 border border-slate-200">
                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Balance Check</p>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-extrabold border ${selected.balanceCheck === 'pass' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-amber-50 text-amber-800 border-amber-200'}`}>
                    {selected.balanceCheck === 'pass' ? 'Pass — Balanced' : 'Pending'}
                  </span>
                  <p className="text-[11px] text-slate-500 mt-1 font-medium">
                    {!selected.invoiceExists ? 'No invoice on file' : fmtNum(selected.balanceAmount) !== 0 ? `Outstanding ${fmtMoney(selected.balanceAmount)}` : 'All invoices cleared'}
                  </p>
                </div>
                <div className="bg-white rounded-xl p-3 border border-slate-200">
                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Created</p>
                  <p className="font-bold text-slate-800">{selected.createdAt ? new Date(selected.createdAt).toLocaleDateString() : '—'}</p>
                </div>
                <div className="bg-white rounded-xl p-3 border border-slate-200">
                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Last Updated</p>
                  <p className="font-bold text-slate-800">{selected.updatedAt ? new Date(selected.updatedAt).toLocaleDateString() : '—'}</p>
                </div>
              </div>

              {selected.removedAt && (
                <div className="p-3 rounded-xl border border-red-200 bg-red-50 text-red-800">
                  <p className="font-bold text-[11px] uppercase tracking-wider mb-1 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Commission Removed</p>
                  <p className="font-medium">
                    Removed {new Date(selected.removedAt).toLocaleDateString()} by {selected.removedBy?.name || selected.removedBy?.email || 'an admin'}.
                    The applicant remains linked to the referrer, who earns nothing for them. This record is kept for history and is excluded from all payout totals.
                  </p>
                </div>
              )}

              {selected.cancelReason && (
                <div className="p-3 rounded-xl border border-rose-200 bg-rose-50 text-rose-800">
                  <p className="font-bold text-[11px] uppercase tracking-wider mb-1 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Cancellation Reason</p>
                  <p className="font-medium">{selected.cancelReason || 'Not provided'}</p>
                </div>
              )}

              {/* Payout info when processed */}
              {selected.payoutStatus !== 'cancelled' && isProcessed(selected) && selected.payment && (
                <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/60">
                  <p className="font-bold text-[11px] uppercase tracking-wider text-emerald-800 mb-2 flex items-center gap-1"><Wallet className="h-3.5 w-3.5" /> Payout Details</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                    <div className="flex justify-between sm:block"><span className="text-emerald-900/60 font-semibold">Transaction ID: </span><strong className="font-mono text-emerald-900">{selected.payment.transactionId || '—'}</strong></div>
                    <div className="flex justify-between sm:block"><span className="text-emerald-900/60 font-semibold">Payout Method: </span><strong className="text-emerald-900">{selected.payment.payoutMethod || '—'}</strong></div>
                    <div className="flex justify-between sm:block"><span className="text-emerald-900/60 font-semibold">Amount: </span><strong className="text-emerald-900">{fmtMoney(selected.payment.amount)}</strong></div>
                    <div className="flex justify-between sm:block"><span className="text-emerald-900/60 font-semibold">Payout Date: </span><strong className="text-emerald-900">{selected.payment.payoutDate ? new Date(selected.payment.payoutDate).toLocaleDateString() : '—'}</strong></div>
                  </div>
                  {selected.payment.note ? <p className="text-xs text-emerald-900 mt-2 italic">“{selected.payment.note}”</p> : null}
                  {selected.payment.attachment && (
                    <a href={selected.payment.attachment.fileUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-emerald-900 bg-emerald-100 hover:bg-emerald-200 px-3 py-1.5 rounded-lg transition">
                      <Paperclip className="h-3.5 w-3.5" /> {selected.payment.attachment.fileName || 'Payment document'}
                    </a>
                  )}
                </div>
              )}

              {/* History */}
              {selected.history && selected.history.length > 0 && (
                <div className="p-3.5 rounded-xl border border-slate-200 bg-slate-50/50">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1"><Info className="h-3 w-3" /> Audit History</p>
                  <ul className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                    {selected.history.slice().reverse().map((h, i) => (
                      <li key={i} className="text-[11px] text-slate-600 flex items-start justify-between gap-2">
                        <span><strong>{h.action || h.toStatus || 'Update'}</strong>{h.note ? ` — ${h.note}` : ''} <span className="text-slate-400">· {h.by?.name || h.by?.email || 'Admin'}</span></span>
                        <span className="text-slate-400 whitespace-nowrap">{h.at ? new Date(h.at).toLocaleDateString() : ''}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 pt-3 border-t border-slate-100">
              {applicantApplication(selected) && onViewApplicant && (
                <button onClick={() => { onViewApplicant(applicantApplication(selected)!); }} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition cursor-pointer flex items-center gap-1.5">
                  <ExternalLink className="h-3.5 w-3.5" /> View Applicant File
                </button>
              )}
              <ActionButtons c={selected} />
              {!selected.removedAt && !isProcessed(selected) && selected.payoutStatus !== 'cancelled' && (
                <button onClick={() => setRemoveFor(selected)} className="px-4 py-2 border border-red-200 text-red-600 hover:bg-red-50 font-bold rounded-xl text-xs transition cursor-pointer">Remove Commission</button>
              )}
              <button onClick={() => setSelected(null)} className="px-4 py-2 border border-slate-200 text-slate-600 font-bold rounded-xl text-xs transition cursor-pointer hover:bg-slate-50">Close</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Cancel modal */}
      {cancelFor && (
        <Modal onClose={() => setCancelFor(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black text-slate-900 flex items-center gap-2">
                <Ban className="h-5 w-5 text-rose-600" /> Cancel Commission
              </h3>
              <button aria-label="Close" title="Close" type="button" onClick={() => setCancelFor(null)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 cursor-pointer"><XCircle className="h-5 w-5" /></button>
            </div>
            <div className="text-xs text-slate-600 space-y-1 bg-slate-50 border border-slate-200 rounded-xl p-3">
              <p>Applicant: <strong>{cancelFor.applicantName}</strong></p>
              <p>Program: <strong>{cancelFor.program}</strong></p>
              <p>Amount: <strong className="font-mono">{fmtMoney(cancelFor.amount)}</strong></p>
              <p>Current: <strong>{commLabel(cancelFor.commissionStatus)}</strong> · <strong>{payLabel(cancelFor.payoutStatus)}</strong></p>
            </div>
            <p className="text-[11px] text-rose-600 flex items-start gap-1.5 bg-rose-50 border border-rose-200 rounded-xl p-3">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              Cancellation is available at any point before payout is processed, and is <strong>terminal</strong> — a cancelled commission cannot be reopened. If you made a mistake, create a new commission record instead.
            </p>
            <div>
              <label className="text-xs font-bold text-slate-600">Reason (optional)</label>
              <textarea value={cancelNote} onChange={e => setCancelNote(e.target.value)} rows={3} placeholder="e.g. Candidate withdrew from the program before placement…" className="mt-1 w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500/20 bg-white" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setCancelFor(null)} className="px-4 py-2 border border-slate-200 text-slate-600 font-bold rounded-xl text-xs hover:bg-slate-50 transition cursor-pointer">Keep Commission</button>
              <button
                onClick={() => runCancel(cancelFor)}
                disabled={saving}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-xl text-xs transition cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
              >
                {saving ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5" />}
                {saving ? 'Cancelling…' : 'Cancel Commission'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Remove commission modal (void — record kept for history) */}
      {removeFor && (
        <Modal onClose={() => setRemoveFor(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black text-slate-900 flex items-center gap-2">
                <Ban className="h-5 w-5 text-red-600" /> Remove Commission
              </h3>
              <button aria-label="Close" title="Close" type="button" onClick={() => setRemoveFor(null)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 cursor-pointer"><XCircle className="h-5 w-5" /></button>
            </div>
            <div className="text-xs text-slate-600 space-y-1 bg-slate-50 border border-slate-200 rounded-xl p-3">
              <p>Applicant: <strong>{removeFor.applicantName}</strong></p>
              <p>Referrer: <strong>{removeFor.referrerName}</strong></p>
              <p>Program: <strong>{removeFor.program}</strong></p>
              <p>Amount: <strong className="font-mono">{fmtMoney(removeFor.amount)}</strong></p>
            </div>
            <p className="text-[11px] text-red-600 flex items-start gap-1.5 bg-red-50 border border-red-200 rounded-xl p-3">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              The commission is removed from this applicant: the applicant <strong>stays linked</strong> to the referrer, who simply earns nothing for them.
              The record is <strong>not deleted</strong> — it stays in the commission history (with your name and the date) and is excluded from all payout totals.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setRemoveFor(null)} className="px-4 py-2 border border-slate-200 text-slate-600 font-bold rounded-xl text-xs hover:bg-slate-50 transition cursor-pointer">Keep Commission</button>
              <button
                onClick={() => runRemove(removeFor)}
                disabled={saving}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-xs transition cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
              >
                {saving ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5" />}
                {saving ? 'Removing…' : 'Remove Commission'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Process payout modal */}
      {processFor && (
        <Modal onClose={() => setProcessFor(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-5 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600"><Wallet className="h-5 w-5" /></div>
                <div>
                  <h3 className="text-base font-black text-slate-900">Mark Payout Processed</h3>
                  <p className="text-[11px] text-slate-500">For queued commission — <strong>{processFor.applicantName}</strong> ({processFor.program})</p>
                </div>
              </div>
              <button aria-label="Close" title="Close" type="button" onClick={() => setProcessFor(null)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 cursor-pointer"><XCircle className="h-5 w-5" /></button>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center justify-between text-xs">
              <span className="text-slate-600 font-semibold">Commission amount</span>
              <span className="font-mono font-black text-emerald-700">{fmtMoney(processFor.amount)}</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs font-bold text-slate-600">Transaction ID <span className="text-rose-500">*</span></label>
                <input value={procTransactionId} onChange={e => setProcTransactionId(e.target.value)} placeholder="e.g. TRX-2026-00921 or bank reference" className="mt-1 w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 bg-white" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600">Payout Method <span className="text-rose-500">*</span></label>
                <select value={procMethod} onChange={e => setProcMethod(e.target.value)} className="mt-1 w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20">
                  <option value="">Select method…</option>
                  {PAYOUT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600">Amount {currency} <span className="text-rose-500">*</span></label>
                <input type="number" min="0" step="0.01" value={procAmount} onChange={e => setProcAmount(e.target.value)} className="mt-1 w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 bg-white" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600">Payout Date <span className="text-rose-500">*</span></label>
                <input type="date" value={procDate} onChange={e => setProcDate(e.target.value)} className="mt-1 w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 bg-white" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-bold text-slate-600">Note (optional)</label>
                <textarea value={procNote} onChange={e => setProcNote(e.target.value)} rows={2} placeholder="e.g. Bank transfer reference 83712…" className="mt-1 w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 bg-white" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-bold text-slate-600">Payment Record / File (optional)</label>
                <input ref={fileInputRef} type="file" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) pickProcFile(f); }} />
                {procFile ? (
                  <div className="mt-1 flex items-center justify-between px-3 py-2 border border-emerald-200 bg-emerald-50/60 rounded-xl">
                    <span className="flex items-center gap-2 text-xs text-emerald-800 font-semibold truncate">
                      <Paperclip className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{procFile.name}</span>
                      <span className="text-[10px] text-emerald-600">({(procFile.size / 1024).toFixed(1)} KB)</span>
                    </span>
                    <button type="button" onClick={() => setProcFile(null)} className="text-rose-500 hover:text-rose-700 font-bold cursor-pointer text-xs">Remove</button>
                  </div>
                ) : (
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="mt-1 w-full border-2 border-dashed border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 rounded-xl px-3 py-3 text-xs text-slate-500 font-semibold transition cursor-pointer flex items-center justify-center gap-2">
                    <Upload className="h-4 w-4" /> Upload payment document / proof
                  </button>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button onClick={() => setProcessFor(null)} className="px-4 py-2 border border-slate-200 text-slate-600 font-bold rounded-xl text-xs hover:bg-slate-50 transition cursor-pointer">Cancel</button>
              <button
                onClick={submitProcessed}
                disabled={saving || uploadingFile}
                className="px-4 py-2 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white font-bold rounded-xl text-xs transition cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {saving || uploadingFile ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                {uploadingFile ? 'Uploading…' : saving ? 'Recording…' : 'Mark Payout Processed'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function RefreshIcon({ loading }: { loading: boolean }) {
  return <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />;
}
